/* eslint-disable prettier/prettier */
import * as React from 'react';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { AppState } from '../../../../app/state/AppState';
import { updateFilter } from '../../../actions/UpdateFilterAction';
import { QuickFilter, QuickFilterDispatchProps, QuickFilterOwnProps, QuickFilterStateProps } from '../QuickFilter';
import { RouteComponentProps } from 'react-router-dom';
import { ActivitySpinnerSize, ActivitySpinnerWidget, DropdownButtonType, LocallyFilteringList, MultiselectDropdownWidget } from '@tableau/widgets';
import { DropdownBlueButton, DropdownDefaultButton, FilteringListClass } from '../../../styles/FilterStyles';
import { CommonStyles } from '../../../../common/styles/CommonStyles';
import { FilterTypes } from '../../../models/FilterTypes';
import { CRASSService } from '../../../services/CRASSService';
import { FilterAllOption } from '../../../state/FilterState';
import { Messages } from '../../../../../gen-src/Localize';
import { FacetResult } from '../../../models/FacetTypes';
import { FilterHelpers } from '../../../../common/utils/FilterHelpers';
import { FeatureFlag, FeatureFlagService } from '../../../services/FeatureFlagService';

export type TagFilterProps = QuickFilterOwnProps & QuickFilterStateProps & QuickFilterDispatchProps & RouteComponentProps;

export interface TagFilterState {
  isLoadingTagList: boolean;
  readonly filterList: FacetResult[];
}

export const delayLoadingTagListDuration = 1000;

export const TagFilterLabelTestID = 'TagFilter';

export class TagFilter extends QuickFilter<TagFilterProps, TagFilterState> {
  private prevValue = this.props.filterSelected.id; //used to track changes in the value selected for telemetry
  private isUnmounted = false;

  constructor(props: TagFilterProps) {
    super(props);
    this.state = { filterList: [], isLoadingTagList: true };
  }

  public componentDidMount(): void {
    this.isUnmounted = false;

    if (FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224)) {
      setTimeout(() => {
        if (!this.isUnmounted) {
          this.loadTagList();
        }
      }, delayLoadingTagListDuration);
    }
  }

  public componentDidUpdate(prevProps: TagFilterProps) {
    if (prevProps.contentType !== this.props.contentType) {
      this.loadTagList();
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true;
  }

  public render() {
    if (!FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224)) {
      return null;
    }

    const buttonStyle =
      this.props.filterSelected.id === FilterAllOption ? CommonStyles.Button.baseButtonStyle : CommonStyles.Button.blueButtonStyle;
    const buttonClass = this.props.filterSelected.id === FilterAllOption ? DropdownDefaultButton : DropdownBlueButton;

    const selectedTags = this.props.filterSelected.id !== FilterAllOption ? this.props.filterSelected.id.split(',') : [];
    let selectedTagDisplayText = '';
    if (selectedTags.length > 0) {
      if (selectedTags.length === 1) {
        selectedTagDisplayText = selectedTags[0];
      } else {
        selectedTagDisplayText = selectedTags[0] + ' +' + (selectedTags.length - 1);
      }
    }

    const maxCount = this.state.filterList.length > 0 ? this.state.filterList[0].count : 0;

    return (
      <MultiselectDropdownWidget
        label={null}
        type={DropdownButtonType.Outline}
        style={{ marginLeft: '10px', minWidth: '100px', maxWidth: '200px', ...buttonStyle }}
        list={
          <LocallyFilteringList
            style={{ maxHeight: window.innerHeight / 2, minWidth: '300px' }}
            items={this.state.isLoadingTagList ? [{
              content: (
                <div style={{ textAlign: 'center', padding: '10px 10px 14px' }}><ActivitySpinnerWidget testId="loading-status" spinnerSize={ActivitySpinnerSize.Small} /></div>
              ),
              value: 'loading',
              displayText: '',
              disabled: true
            }] : FilterHelpers.getFilterValues(this.state.filterList, undefined, undefined, true, maxCount)}
            itemFilterFn={(item, filterValue) => item.value?.toLowerCase().indexOf(filterValue) !== -1}
            searchPlaceholder={Messages.TagInputPlaceholder()}
            className={FilteringListClass}
          />
        }
        className={buttonClass}
        onChange={(value) => this.onChange(value)}
        value={new Set(this.props.filterSelected.id.split(','))}
      >
        {selectedTagDisplayText ?
          <div data-tb-test-id={TagFilterLabelTestID} style={{ ...CommonStyles.Layout.TextEllipsis }}>
            <b>{selectedTagDisplayText}</b>
          </div>
          : <div data-tb-test-id={TagFilterLabelTestID}>
            <span>{Messages.TagSubTitle() + ' '}</span>
            <b>{Messages.TagFilterAllOption()}</b>
          </div>}
      </MultiselectDropdownWidget>
    );
  }

  private onChange(tag: string) {
    const valueString = this.props.filterSelected.id === FilterAllOption ? [] : this.props.filterSelected.id.split(',');
    const selectedTags = new Set(valueString);
    if (!selectedTags.has(tag)) {
      selectedTags.add(tag);
    } else {
      selectedTags.delete(tag);
    }

    const newValueString = Array.from(selectedTags).join(',');

    this.sendFiltersUpdatedTelemetryEvent(newValueString, this.prevValue, FilterTypes.tag, this.props.contentType);
    this.onDropDownValueChanged(newValueString || FilterAllOption, FilterTypes.tag, newValueString);
    this.prevValue = newValueString;
  }

  private loadTagList() {
    if (!this.isUnmounted) {
      this.setState({ isLoadingTagList: true });
    }
    CRASSService.instance.getTagList(this.props.searchTerm, this.props.contentType).then((result) => {
      if (!this.isUnmounted) {
        const filterList = result ? result : [];
        const tagToAppend: string[] = [];
        this.props.filterSelected.id.split(',').forEach(tag => {
          if (tag !== FilterAllOption) {
            if (filterList.filter(item => item.value.id === tag).length === 0) {
              tagToAppend.push(tag);
            }
          }
        });

        tagToAppend.forEach(tag => {
          filterList.push({ value: { name: tag, id: tag }, count: 0 });
        });

        const sortedFilterList = filterList.sort((a, b) => {
          if (a.count === b.count) {
            return a.value.id.toLowerCase().localeCompare(b.value.id.toLowerCase());
          }
          return b.count - a.count;
        });

        this.setState({ filterList: sortedFilterList, isLoadingTagList: false });
      }
    });
  }
}

const mapStateToProps = (state: AppState) => ({
  filterSelected: state.filters.query.tag,
  allFilterValues: state.filters.query,
  contentType: state.search.selectedContentTypeFilterValue,
  mixedContentResults: state.search.mixedContentType.searchResult,
  singleContentResults: state.singleContentType.searchResults,
  isLoadingFilterContent: state.filters.isLoadingFilterContent,
  searchTerm: state.search.searchTerm,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateFilter,
    },
    dispatch
  );

const TagFilterWithRouter = withRouter(TagFilter);

export const ConnectedTagFilter: React.ComponentClass<QuickFilterOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(TagFilterWithRouter);
